import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';

/**
 * "عروضي" (My Promos) — the café's own designs, shown as a rotation on top
 * of whatever is playing. Backend: VenueOverlayPromoController (storage/CRUD
 * only — the device runs the rotation locally, see
 * overlay_billing_defect_and_arbiter_scope.md's sibling memory for why there
 * is no live "is this showing right now" signal to read here) and the
 * already-hardware-proven VenueOverlayController for the one-shot fire.
 *
 *   GET    /venue-overlay/promos            → { data: [...] }
 *   GET    /venue-overlay/promos/{id}        → { data: {...} }
 *   POST   /venue-overlay/promos/create      → { data: {...} }  (201)
 *   PUT    /venue-overlay/promos/{id}/update  → { data: {...} }
 *   DELETE /venue-overlay/promos/{id}/delete  → { data: {status,msg} }
 *   POST   /venue-overlay/show               → { data: {status,msg,skipped} }
 */

const LIST_KEY = 'venueOverlayPromos';

export const useGetPromos = () =>
  useQuery(
    LIST_KEY,
    async () => {
      const res = (await axiosInstance.get('/venue-overlay/promos')).data;
      return res?.data ?? res ?? [];
    },
    {
      staleTime: 15_000,
      // ⚠️ REGRESSION FIX (2026-09-10). The dashboard used to "self-refresh"
      // when a promo finished purely because "finished" was, until now, a
      // function of elapsed time alone — Promos.jsx's own 30s `now` tick was
      // enough on its own, no new server data required. real_finished_at (a
      // CONFIRMED device report, see promoRuntimeState's own doc) breaks
      // that: it can only ever arrive via a fresh fetch, never by advancing
      // the local clock. Without this, a promo that genuinely finishes now
      // sits showing "running" until someone manually reloads the page.
      // Polling on the same 30s cadence the clock already ticks on restores
      // the self-refreshing behaviour, correctly, for the new source too.
      refetchInterval: 30_000,
    }
  );

export const useCreatePromo = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    (payload) => axiosInstance.post('/venue-overlay/promos/create', payload),
    {
      onSuccess: () => qc.invalidateQueries(LIST_KEY),
      onError: (err) => showToast('error', 'عروضي', err.response?.data?.message || err.response?.data?.errors),
    }
  );
};

export const useUpdatePromo = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    ({ id, ...payload }) => axiosInstance.put(`/venue-overlay/promos/${id}/update`, payload),
    {
      onSuccess: () => qc.invalidateQueries(LIST_KEY),
      onError: (err) => showToast('error', 'عروضي', err.response?.data?.message || err.response?.data?.errors),
    }
  );
};

export const useDeletePromo = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    (id) => axiosInstance.delete(`/venue-overlay/promos/${id}/delete`),
    {
      onSuccess: () => qc.invalidateQueries(LIST_KEY),
      onError: (err) => showToast('error', 'عروضي', err.response?.data?.message),
    }
  );
};

/**
 * "اعرضه الآن" — fires the FIRST design once, immediately, outside the
 * schedule. Wired to the same POST /venue-overlay/show that was hardware-
 * proven live on APC390 (corner overlay, campaign+audio untouched).
 *
 * A promo can target several devices (or "all company devices" when
 * device_ids is null) but /venue-overlay/show takes exactly one device per
 * call — so this fires one call PER TARGET DEVICE, in parallel. Partial
 * failure is real and reported: one device being busy (an overlay already
 * showing there — see AdPlacementService::placeVenueOverlay's hasActiveOverlay
 * guard, `skipped:true`) must not look like total failure to the owner.
 */
export const useShowPromoNow = () => {
  const { showToast } = useCustomToast();
  return useMutation(
    async ({ mediaId, deviceIds, shape, duration }) => {
      const results = await Promise.allSettled(
        deviceIds.map((deviceId) =>
          axiosInstance.post('/venue-overlay/show', {
            media_id: mediaId,
            device_id: deviceId,
            shape,
            duration,
          })
        )
      );
      const skipped = results.filter(
        (r) => r.status === 'fulfilled' && r.value?.data?.data?.skipped
      ).length;
      const failed = results.filter((r) => r.status === 'rejected').length;
      return { total: deviceIds.length, skipped, failed };
    },
    {
      onError: (err) => showToast('error', 'عروضي', err.response?.data?.message),
    }
  );
};

/** "على أي أجهزة" reuses the same company-devices list every other picker on the platform does. */
export { useGetAllCompanyDevices } from './devices';

/** "التصاميم" — pick from the existing media library, images only (GIF included — same MIME family). */
export const useGetPromoDesignLibrary = (enabled = true) =>
  useQuery(
    'promoDesignLibrary',
    async () => (await axiosInstance.get('/folder/media', {
      params: { section: 'MEDIA', type: 'IMAGE', recursive: 1, per_page: 200, page: 1 },
    })).data,
    { enabled, staleTime: 30_000 }
  );

/* ── Template kit (Build A): QR links + media record for rendered designs ─────
 *   GET  /venue-overlay/qr-links                 → { data: [{id, code, short_url, type, target_url, display, promo_id, is_disabled, scans}] }
 *   POST /venue-overlay/qr-links  {type,input,promo_id?}
 *   PUT  /venue-overlay/qr-links/{id} {input?, promo_id?, is_disabled?}   (destination change = target_url only, code never changes)
 * Storage/validation only; the public redirect is GET /q/{code} on the API host. */
const QR_KEY = 'promoQrLinks';

export const useGetQrLinks = () =>
  useQuery(
    QR_KEY,
    async () => {
      const res = (await axiosInstance.get('/venue-overlay/qr-links')).data;
      return res?.data ?? [];
    },
    { staleTime: 15_000 }
  );

export const createQrLink = async ({ type, input, promo_id = null }) =>
  (await axiosInstance.post('/venue-overlay/qr-links', { type, input, promo_id })).data?.data;

export const updateQrLink = async (id, body) =>
  (await axiosInstance.put(`/venue-overlay/qr-links/${id}`, body)).data?.data;

/** Same POST the media page uses after uploadToGcs (queries/folderMedia.js createMedia). Returns the new media id. */
export const createMediaRecord = async ({ name, size, path, folderId = null }) => {
  const { data } = await axiosInstance.post('/media/create', {
    folder_id: folderId, type: 'media', name, size, path,
  });
  return data?.data?.media_file ?? data?.media_file ?? null;
};
